import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { ShareField } from '@/components/dialogs/ShareDialog/ShareField';
import { useGetFeatureFlagQuery, useUpdateFeatureFlagMutation } from './api/featureFlagsApi';
import { FEATURE_FLAG_TARGETS } from './targets';
import { FeatureFlagTargetChip } from './FeatureFlagTargetChip';
import { useAdminConfirm } from '../shared/AdminConfirmProvider';
import { useFeatureFlagRules } from './useFeatureFlagRules';
import { getTargetName } from '@/admin/featureFlags/getTargetName';

const DIALOG_NAME = 'addFeatureFlagTargets';

// The dialog itself, mounted fresh for each flag and field, so its draft always starts from
// that cell's saved list.
const FeatureFlagTargetsEditor = ({ flag, field, onClose }) => {
	const dispatch = useDispatch();
	const { canRemove } = useFeatureFlagRules(flag, field);
	const confirm = useAdminConfirm();
	const [updateFeatureFlag, { isLoading }] = useUpdateFeatureFlagMutation();
	const [draftIds, setDraftIds] = useState(flag[field] ?? []);

	const { noun, Picker } = FEATURE_FLAG_TARGETS[field];
	const savedIds = flag[field] ?? [];
	const add = draftIds.filter((id) => !savedIds.includes(id));
	const remove = savedIds.filter((id) => !draftIds.includes(id));
	// Saved entries that can't be removed; ones added in this draft can still be taken out
	const isLocked = (id) => savedIds.includes(id) && !canRemove(id);

	const handleSave = async () => {
		const [addNames, removeNames] = await Promise.all(
			[add, remove].map((ids) =>
				Promise.all(ids.map((id) => getTargetName(dispatch, field, id))),
			),
		);
		const confirmed = await confirm({
			title: `Update ${flag.feature}`,
			content: (
				<Stack spacing={2}>
					{addNames.length > 0 && (
						<Typography>
							{`Adding the following ${noun}:`}
							<br />
							{addNames.join(', ')}
						</Typography>
					)}
					{removeNames.length > 0 && (
						<Typography>
							{`Removing the following ${noun}:`}
							<br />
							{removeNames.join(', ')}
						</Typography>
					)}
				</Stack>
			),
			confirmText: 'Save',
		});
		if (!confirmed) return;

		const result = await updateFeatureFlag({ id: flag.id, [field]: { add, remove } });
		if (result.error) {
			dispatch(
				showToast({
					message: `Couldn't update ${noun} for ${flag.feature}. Please try again.`,
					error: true,
				}),
			);
			return;
		}
		onClose();
	};

	return (
		<Dialog
			open
			onClose={onClose}
			slotProps={{ paper: { sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } } } }}
		>
			<DialogTitle>{`${noun[0].toUpperCase()}${noun.slice(1)} for ${flag.feature}`}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<ShareField label={`Turn ${flag.feature} on for these ${noun}`}>
						{draftIds.length > 0 && (
							<Stack
								direction='row'
								flexWrap='wrap'
								gap={0.5}
								sx={{ mt: 0.5, mb: 1 }}
							>
								{draftIds.map((id) => (
									<FeatureFlagTargetChip
										key={id}
										field={field}
										id={id}
										{...(field === 'users' && { showEmail: true })}
										{...(!isLocked(id) && {
											onDelete: () =>
												setDraftIds((ids) =>
													ids.filter((draftId) => draftId !== id),
												),
										})}
									/>
								))}
							</Stack>
						)}
						<Picker
							value={draftIds}
							onChange={setDraftIds}
							showCheckbox
							isOptionFixed={(option) => isLocked(option.id)}
							// The chips above show the selection, so the input doesn't repeat them
							renderValue={() => null}
						/>
					</ShareField>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={handleSave}
					disabled={(!add.length && !remove.length) || isLoading}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Edits a feature flag's users, broker groups or ledgers. The dialog holds a draft of the
 * list: the chips and the ticked options in the dropdown both show it, ticking or unticking
 * an option or clicking a chip's × changes it, and Save sends what was added and removed
 * after the admin confirms. Saved entries the rules don't allow removing (the admin
 * themselves on adminModeEnabled) stay locked in the draft. Opened, like ShareDialog, with
 * openDialog({ dialogName: 'addFeatureFlagTargets', data: { flagId, field } }). Rendered once
 * by the list page, outside the grid, so typing in the picker isn't caught by the grid's
 * keyboard handling.
 */
export const AddFeatureFlagTargetsDialog = () => {
	const dispatch = useDispatch();
	const open = useSelector((state) => state.dialog.open[DIALOG_NAME]);
	const { flagId, field } = useSelector((state) => state.dialog.data[DIALOG_NAME]);
	const { data } = useGetFeatureFlagQuery(flagId, { skip: !flagId });
	// `data` can still hold the previously opened flag for a moment, so only use it once it's
	// the flag that was clicked
	const flag = data?.id === flagId && data;

	if (!open || !flag) {
		return null;
	}

	return (
		<FeatureFlagTargetsEditor
			key={`${flagId}:${field}`}
			flag={flag}
			field={field}
			onClose={() => dispatch(closeDialog(DIALOG_NAME))}
		/>
	);
};
